'use client';

import { VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { BodyDefault, MetaDefault } from '../../../../components/typography';
import { SettingsCard } from '../../SettingsCard';
import { SettingsRow } from '../../SettingsRow';

export type DynamicApplicationScanRunSummary = {
  status: string;
  completedAt?: Date | string | null;
  error?: string | null;
};

export type DynamicApplicationScanLastRunCardProps = {
  lastRun: DynamicApplicationScanRunSummary;
};

const LAST_RUN_STATUS_KEYS: Readonly<
  Record<string, `lastRun.status.${'COMPLETED' | 'FAILED' | 'STOPPED'}`>
> = {
  COMPLETED: 'lastRun.status.COMPLETED',
  FAILED: 'lastRun.status.FAILED',
  STOPPED: 'lastRun.status.STOPPED',
};

const hasStoppedReason = (lastRun: DynamicApplicationScanRunSummary) =>
  lastRun.status === 'STOPPED' &&
  Boolean(lastRun.error) &&
  lastRun.error?.trim().toLowerCase() !== 'stopped';

export function DynamicApplicationScanLastRunCard({
  lastRun,
}: DynamicApplicationScanLastRunCardProps) {
  const t = useTranslations('designSystem.automation.dynamicApplicationScan');
  const statusKey = LAST_RUN_STATUS_KEYS[lastRun.status];
  const statusLabel = statusKey ? t(statusKey) : lastRun.status;
  const showFailedError = lastRun.status === 'FAILED' && Boolean(lastRun.error);

  return (
    <SettingsCard>
      <SettingsRow
        label={t('lastRun.title')}
        valueWrap
        value={
          <VStack alignItems="stretch" gap={1}>
            <BodyDefault>{statusLabel}</BodyDefault>
            {lastRun.completedAt ? (
              <MetaDefault>
                {new Date(lastRun.completedAt).toLocaleString()}
              </MetaDefault>
            ) : null}
            {showFailedError ? (
              <MetaDefault color="fg.error" textAlign="left" w="full">
                {lastRun.error}
              </MetaDefault>
            ) : null}
            {hasStoppedReason(lastRun) ? (
              <MetaDefault textAlign="left" w="full">
                {lastRun.error}
              </MetaDefault>
            ) : null}
          </VStack>
        }
      />
    </SettingsCard>
  );
}
